import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  Aangifte, BokkzAlgDataService, IncomeTaxEntity, JaarrekeningBalans,
  Onderneming
} from '@snelstart-ventures/fiscaal/domain-logic';
import { BehaviorSubject, filter, Subscription, take } from 'rxjs';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { CommonModule, DecimalPipe } from '@angular/common';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-for',
  templateUrl: './spec-for.component.html',
  imports: [
    SharedUiFormControlsModule,
    SharedUiAlertModule,
    DecimalPipe,
    SharedUiDialogModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ],
  styleUrls: ['./spec-for.component.scss']
})

export class SpecForComponent implements OnInit, OnDestroy {
  private routerSubscription: Subscription | undefined;
  private incomeTaxEntitySubscription: Subscription | undefined;
  private clientEntitySubscription: Subscription | undefined;
  private isSaving = false;
  @Input() public id!: string;
  public clientId!: string;
  public incomeTaxEntity = {} as IncomeTaxEntity;
  public partnerBoolean = {} as boolean;
  @Input() partner = new BehaviorSubject<boolean>(false);

  public showSpinner = true;
  public focusedElementId: string | null = null;
  public dialogVisible = false;

  public ondernemingFG: FormGroup;
  public specFORFG = {} as FormGroup;
  public formStructure: any;
  public ondernemingLength: any;

  showDialog() {
    this.dialogVisible = true;
  }

  constructor(private fb: FormBuilder,
              private router: Router,
              public dataService: BokkzIbDataService, private dataAlgService: BokkzAlgDataService) {
    this.ondernemingFG = this.fb.group({
      ondernemingen: this.fb.array([this.createOndernemingFormGroup()])
    });
    this.specFORFG = this.fb.group<JaarrekeningBalans>({
      waardeOpening: null as unknown as number,
      waardeSlot: null as unknown as number
    });

  }

  //onderneming
  get ondernemingen(): FormArray {
    return this.ondernemingFG.get('ondernemingen') as FormArray;
  }

  createOndernemingFormGroup(onderneming?: Onderneming) {
    return this.dataService.createOndernemingFormGroup(onderneming);
  }

  getTotaal(): number {
    const start = this.specFORFG.get('waardeOpening')?.getRawValue() || 0;
    let dotatie = 0;
    let onttrekking = 0;
    this.partner.subscribe((partner) => {
      if (!partner) {
        dotatie = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0].dotatieFOR || 0;
        onttrekking = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0].vrijvalFOR || 0;
      } else {
        dotatie = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0].dotatieFOR || 0;
        onttrekking = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0].vrijvalFOR || 0;
      }
    });
    const einde = this.specFORFG.get('waardeSlot')?.getRawValue() || 0;
    const result = start + dotatie - onttrekking - einde;
    return Math.abs(result);
  }

  getForBeginstand(): number {
    const start = this.specFORFG.get('waardeOpening')?.getRawValue() || 0;
    return start;
  }

  getForEindstand(): number {
    const eind = this.specFORFG.get('waardeSlot')?.getRawValue() || 0;
    return eind;
  }

  ngOnInit(): void {
    this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
      this.incomeTaxEntity = incomeTaxEntity;
      if (incomeTaxEntity.aangifte) {
        this.partner.subscribe((partner) => {
          this.partnerBoolean = partner;
          this.updateForm();
        });
        this.ondernemingFG.valueChanges.subscribe(value => {
          this.formStructure = value;
        });
        this.ondernemingen.valueChanges.subscribe(value => {
          this.ondernemingLength = value;
        });
      }
    });
    this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
      this.clientId = client.sstRelationId!;
    });
    this.routerSubscription = this.router.events.pipe(filter((event: Event): event is NavigationEnd =>
      event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.handleUrlChange(event.urlAfterRedirects);
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
    }
    if (this.incomeTaxEntitySubscription) {
      this.incomeTaxEntitySubscription.unsubscribe();
    }
    if (this.clientEntitySubscription) {
      this.clientEntitySubscription.unsubscribe();
    }
  }

  handleUrlChange(newUrl: string) {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
      this.isSaving = false;
    }
  }

  private hasUnsavedChanges(): boolean {
    return this.ondernemingFG.dirty || this.specFORFG.dirty;
  }

  private updateForm(): void {
    let ondernemingenLijst = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen || [];

    this.partner.pipe(take(1)).subscribe((partner) => {
      if (partner) {
        ondernemingenLijst = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
      }
    });

    const formArray = this.ondernemingFG.get('ondernemingen') as FormArray;
    const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

    while (formArray.length > ondernemingenLijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    // Update ondernemingen
    for (let i = 0; i < ondernemingenLijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(ondernemingenLijst[i]);
      } else {
        const formGroup = this.createOndernemingFormGroup(ondernemingenLijst[i]);
        formArray.push(formGroup);
      }
    }

    if (focusedControl && document.contains(focusedControl)) {
      focusedControl.focus();
    }

    const onderneming = !this.partnerBoolean
      ? this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0]
      : this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0];

    if (onderneming) {
      this.specFORFG.reset();
      const oudedagsreserve = onderneming?.eigenVermogen?.filter(item => item !== null).find(item => item.omschrijving === 'oudedagsReserve') as JaarrekeningBalans;
      this.specFORFG.patchValue(oudedagsreserve);
    }
  }


  saveForm(): void {
    this.partner.pipe(take(1)).subscribe((partner) => {
      const ondernemingen = !partner
        ? this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen
        : this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen;

      if (ondernemingen && ondernemingen.length > 0) {
        // Haal de waarde van vrijvalFOR uit het formulier voor de eerste onderneming
        const vrijvalFORWaarde = this.ondernemingFG.get('ondernemingen')?.getRawValue()[0]?.vrijvalFOR || 0;
        const beginstandFORWaarde = this.specFORFG.get('waardeOpening')?.getRawValue() || 0;

        // Sla de waarde correct op in de onderneming
        ondernemingen[0].vrijvalFOR = vrijvalFORWaarde;
        ondernemingen[0].beginstandFOR = beginstandFORWaarde;

        // Update de incomeTaxEntity
        this.incomeTaxEntity.aangifte = {
          ...this.incomeTaxEntity.aangifte,
          ...(partner && {
            partnerAangifte: {
              ...this.incomeTaxEntity.aangifte?.partnerAangifte
            }
          })
        } as Aangifte;

        // Stuur de update naar de service
        this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
      }
    });
  }

  public heeftOndernemingen(): boolean {
    if (this.partnerBoolean) {
      return !!(this.incomeTaxEntity &&
        this.incomeTaxEntity.aangifte?.partnerAangifte &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1 &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming.ondernemingen &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming.ondernemingen.length > 0);
    } else {
      return !!(this.incomeTaxEntity.aangifte &&
        this.incomeTaxEntity.aangifte?.box1 &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming.ondernemingen &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming.ondernemingen.length > 0);
    }
  }

}

